import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaUsers,
  FaCheckCircle,
  FaSpinner,
  FaExclamationTriangle,
  FaCalendarAlt,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Batch = {
  id: number;
  name: string;
  topic: string;
};

type ShadowingTask = {
  id: number;
  level: string;
  taskNumber: number;
  taskDescription: string;
  audioDownloadUrl: string;
};

type AssignedBatchWithDeadline = {
  batchId: number;
  batchName: string;
  deadline: string;
};

const AssignShadowingTaskPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();

  const [task, setTask] = useState<ShadowingTask | null>(null);
  const [allBatches, setAllBatches] = useState<Batch[]>([]);
  const [assignedBatchesWithDeadlines, setAssignedBatchesWithDeadlines] =
    useState<AssignedBatchWithDeadline[]>([]);
  const [selectedBatches, setSelectedBatches] = useState<number[]>([]);
  const [deadline, setDeadline] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchData();
  }, [taskId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const taskResponse = await fetch(
        `${API_BASE_URL}/api/admin/shadowing-tasks/${taskId}`,
        { credentials: "include" }
      );
      if (taskResponse.ok) {
        const taskData = await taskResponse.json();
        setTask(taskData);

        const batchesResponse = await fetch(
          `${API_BASE_URL}/api/admin/shadowing-tasks/active-batches`,
          { credentials: "include" }
        );
        if (batchesResponse.ok) {
          const batchesData = await batchesResponse.json();
          setAllBatches(batchesData);
        }

        const assignmentsWithDeadlinesResponse = await fetch(
          `${API_BASE_URL}/api/admin/shadowing-tasks/${taskId}/assigned-batches`,
          { credentials: "include" }
        );
        if (assignmentsWithDeadlinesResponse.ok) {
          const deadlineData = await assignmentsWithDeadlinesResponse.json();
          setAssignedBatchesWithDeadlines(deadlineData);
        }
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const assignedBatchIds = assignedBatchesWithDeadlines.map(
    (assigned) => assigned.batchId
  );

  const filteredBatches = allBatches.filter(
    (batch) =>
      batch.topic.toUpperCase() === task?.level.toUpperCase() &&
      !assignedBatchIds.includes(batch.id)
  );

  const handleBatchToggle = (batchId: number) => {
    setSelectedBatches((prev) =>
      prev.includes(batchId)
        ? prev.filter((id) => id !== batchId)
        : [...prev, batchId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (selectedBatches.length === 0) {
      setError("Please select at least one batch");
      return;
    }
    if (!deadline) {
      setError("Please select a deadline");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/shadowing-tasks/${taskId}/assign-batches`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            batchIds: selectedBatches,
            deadline: deadline,
          }),
        }
      );

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/admin/shadowing-tasks");
        }, 1500);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(
          data.error || "Failed to assign task. It may already be assigned."
        );
      }
    } catch (err) {
      console.error("Error assigning task:", err);
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDeadline = (deadlineStr: string) => {
    return new Date(deadlineStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaUsers />}
        title="Assign Shadowing Task to Batches"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Shadowing Tasks", to: "/admin/shadowing-tasks" },
          { label: "Assign" },
        ]}
      />

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {success && (
          <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <FaCheckCircle className="text-green-600 text-xl" />
            <div>
              <h3 className="font-semibold text-green-800">Success!</h3>
              <p className="text-sm text-green-700">
                Shadowing task assigned successfully. Redirecting...
              </p>
            </div>
          </div>
        )}

        {/* Task Info Card */}
        {task && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Task Details
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-700">Task #:</span>
                <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 font-bold rounded-md text-sm">
                  {task.taskNumber}
                </span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Level: </span>
                <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                  {task.level}
                </span>
              </div>
              {task.taskDescription && (
                <div>
                  <span className="font-semibold text-gray-700">
                    Description:{" "}
                  </span>
                  <p className="text-gray-600 mt-1 whitespace-pre-wrap">
                    {task.taskDescription}
                  </p>
                </div>
              )}
              <div>
                <span className="font-semibold text-gray-700 block mb-2">
                  Audio Preview:
                </span>
                <audio controls className="w-full" src={task.audioDownloadUrl}>
                  Your browser does not support audio playback.
                </audio>
              </div>
            </div>
          </div>
        )}

        {/* Already Assigned Batches with Deadlines */}
        {assignedBatchesWithDeadlines.length > 0 && (
          <div className="mb-6 bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaCheckCircle className="text-green-600" />
              Already Assigned Batches
            </h3>
            <div className="space-y-2">
              {assignedBatchesWithDeadlines.map((assigned) => (
                <div
                  key={assigned.batchId}
                  className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-600 text-sm" />
                    <span className="font-medium text-gray-900">
                      {assigned.batchName}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FaCalendarAlt className="text-orange-500" />
                    <span className="font-medium">
                      Deadline: {formatDeadline(assigned.deadline)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Assignment Form */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Select Batches */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select Batches * (Level: {task?.level})
              </label>
              {filteredBatches.length === 0 ? (
                <div className="text-center py-8 bg-amber-50 border border-amber-200 rounded-lg">
                  <FaExclamationTriangle className="mx-auto text-amber-500 text-3xl mb-2" />
                  <p className="text-amber-700 font-semibold">
                    No {task?.level} level batches available
                  </p>
                  <p className="text-sm text-amber-600 mt-1">
                    {assignedBatchesWithDeadlines.length > 0
                      ? `All ${task?.level} batches have already been assigned this task.`
                      : `Create a batch with topic "${task?.level}" to assign this task.`}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {filteredBatches.map((batch) => (
                    <label
                      key={batch.id}
                      className="flex items-center gap-3 p-3 rounded-lg transition-colors bg-gray-50 hover:bg-blue-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBatches.includes(batch.id)}
                        onChange={() => handleBatchToggle(batch.id)}
                        className="w-5 h-5 text-[#009fe3] focus:ring-[#009fe3] rounded"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {batch.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          Topic: {batch.topic}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-2">
                Selected: {selectedBatches.length} batch(es) • Already assigned:{" "}
                {assignedBatchesWithDeadlines.length} batch(es)
              </div>
            </div>

            {/* Deadline */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Submission Deadline *
              </label>
              <input
                type="datetime-local"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
                style={{ fontSize: "16px" }}
                required
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={
                  submitting ||
                  success ||
                  filteredBatches.length === 0 ||
                  selectedBatches.length === 0
                }
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <FaUsers />
                    Assign to Batches
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin/shadowing-tasks")}
                disabled={submitting}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AssignShadowingTaskPage;
